/**
 * Global Configuration & Utilities
 */
const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

const safeWindowOrigin = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.origin;
};

const safeWindowWsBase = (): string => {
  if (typeof window === "undefined") return "";
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}`;
};

const API_BASE_URL = trimTrailingSlash(import.meta.env.VITE_API_BASE_URL || "");
const WS_BASE_URL = trimTrailingSlash(import.meta.env.VITE_WS_BASE_URL || "");
const PUBLIC_APP_ORIGIN = trimTrailingSlash(import.meta.env.VITE_PUBLIC_APP_ORIGIN || safeWindowOrigin());
export const SMOKE_MODE =
  import.meta.env.VITE_SMOKE_MODE === "1" || API_BASE_URL.includes("jamroom-api-smoke");

export const buildApiUrl = (path: string): string => {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
};

export const buildAccessLoginUrl = (returnToUrl: string): string => {
  // Use a Worker endpoint that redirects to Cloudflare Access login (when enabled),
  // then returns back to Pages.
  return buildApiUrl(`/auth/bridge?redirect_url=${encodeURIComponent(returnToUrl)}`);
};

export const buildAccessLogoutUrl = (returnToUrl: string): string => {
  // Clears Cloudflare Access session for this app, then returns back to Pages.
  // Prefer the Access logout endpoint on the API origin so it works even when only `/auth/*` is protected.
  if (API_BASE_URL) {
    return `${API_BASE_URL}/cdn-cgi/access/logout?redirect_url=${encodeURIComponent(returnToUrl)}`;
  }
  return buildApiUrl(`/auth/logout?redirect_url=${encodeURIComponent(returnToUrl)}`);
};

export const buildWsSessionUrl = (sessionId: string, token?: string): string => {
  const encodedSessionId = encodeURIComponent(sessionId);
  const base = WS_BASE_URL || safeWindowWsBase();
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${base}/ws/s/${encodedSessionId}${qs}`;
};

export const buildShareUrl = (sessionId: string): string => {
  const encodedSessionId = encodeURIComponent(sessionId);
  return `${PUBLIC_APP_ORIGIN}/s/${encodedSessionId}`;
};

// Audio Configuration
export const AUDIO_CONFIG = {
  SAMPLE_RATE_TARGET: 48000,
  BIT_DEPTH: 16,
  CHANNELS: 1,
  BLOCK_SIZE: 4096,
  ENABLE_ECHO_CANCELLATION: false,
  ENABLE_NOISE_SUPPRESSION: false,
  ENABLE_AUTO_GAIN: false,
};

// UI Configuration
export const UI_CONFIG = {
  ZOOM_MIN: 5,
  ZOOM_MAX: 500,
  ZOOM_DEFAULT: 30,
  TRACK_LANE_HEIGHT: 100,
  TRACK_HEADER_WIDTH: 200,
  TRANSPORT_HEIGHT: 60,
  TIMELINE_RULER_HEIGHT: 24,
};

// Network Configuration
export const NETWORK_CONFIG = {
  API_TIMEOUT: 30000,
  UPLOAD_CHUNK_SIZE: 1024 * 1024, // 1MB chunks
  MAX_FILE_SIZE: 100 * 1024 * 1024, // 100MB
  PRESIGN_RETRY_COUNT: 3,
  PRESIGN_RETRY_DELAY: 1000,
};

// Debug Mode
export const DEBUG = {
  ENABLED: import.meta.env.DEV,
  VERBOSE_LOGGING: false,
  MOCK_RECORDING: false, // Use fake audio for testing
};

// Utility functions
export const logDebug = (module: string, message: string, data?: any) => {
  if (DEBUG.ENABLED && DEBUG.VERBOSE_LOGGING) {
    console.log(`[${module}] ${message}`, data || "");
  }
};

export const logError = (module: string, message: string, error?: any) => {
  console.error(`[${module}] ${message}`, error || "");
};
